import { IAssetDTO } from '@/dtos';
import {
  uploadTermsAPI,
  uploadMovieAPI,
  uploadTeamLogoAPI,
  uploadContestMovieAPI,
  deleteContestAPI,
  saveAvatarAPI,
  uploadContestReleaseAPI,
  deleteReleaseFromContestAPI,
  uploadSubmissionReleaseAPI,
  uploadTeamVideoAPI,
  uploadScreenShotAPI,
  deleteReleaseFromSubmissionAPI,
  uploadScreeningCopyAPI,
  getVideoAssetAPI,
  uploadBTSVideoAPI,
} from '@/services';
import { Ref } from 'vue';

export interface IUploadStatus {
  type: 'error' | 'success' | 'info' | 'warning' | undefined;
  message: string;
}

export const UPLOAD_NOFILE = { type: 'error', message: 'No file selected' } as IUploadStatus;
export const UPLOAD_SUCCESS = { type: 'success', message: 'Upload successful!' } as IUploadStatus;
export const UPLOAD_ERROR = (error: string) => {
  return { type: 'error', message: `Upload failed: ${error}` } as IUploadStatus;
};

export const createProgressHandler = (
  progressRef: Ref<number | null>,
  completedRef: Ref<boolean>,
) => {
  return async (progress: number) => {
    if (progress >= 100) {
      progressRef.value = null;
      completedRef.value = true;
    } else {
      progressRef.value = progress;
    }
  };
};

export async function uploadTerms(file: Blob, onProgress?: (percentComplete: number) => void) {
  try {
    const terms = await uploadTermsAPI(file, onProgress);
    return terms;
  } catch (error) {
    console.log(error);
    throw 'error saving terms';
  }
}

export async function uploadScreenShot(
  teamId: string,
  yearId: string,
  submissionId: string,
  file: File,
  onProgress: (percentComplete: number) => void,
) {
  try {
    const terms = await uploadScreenShotAPI(teamId, yearId, submissionId, file, onProgress);
    return terms;
  } catch {
    throw 'Error uploading ScreenShot';
  }
}

export async function uploadMovie(
  teamId: string,
  yearId: string,
  submissionId: string,
  file: File,
  onProgress: (percentComplete: number) => void,
) {
  try {
    const movie = await uploadMovieAPI(teamId, yearId, submissionId, file, onProgress);
    return movie;
  } catch (error) {
    console.log(error);
    throw 'Error uploading Movie';
  }
}
export async function uploadBTSVideo(
  teamId: string,
  yearId: string,
  submissionId: string,
  file: File,
  onProgress: (percentComplete: number) => void,
) {
  try {
    const movie = await uploadBTSVideoAPI(teamId, yearId, submissionId, file, onProgress);
    return movie;
  } catch (error) {
    console.log(error);
    throw 'Error uploading team video';
  }
}
export async function uploadTeamVideo(
  teamId: string,
  yearId: string,
  submissionId: string,
  file: File,
  onProgress: (percentComplete: number) => void,
) {
  try {
    const movie = await uploadTeamVideoAPI(teamId, yearId, submissionId, file, onProgress);
    return movie;
  } catch (error) {
    console.log(error);
    throw 'Error uploading team video';
  }
}

export async function getVideoHlsStream(
  assetId: string,
  maxRetries: number = 20,
): Promise<IAssetDTO | null> {
  const response = await getVideoAsset(assetId);
  const retryDelay = 10000;

  const { hls_url } = response;

  if (!hls_url || !hls_url.match(/^http/)) {
    if (maxRetries <= 0) {
      console.log('Max retries reached, returning null response without HLS URL');
      return null;
    }

    console.log(`Retrying HLS fetch in ${retryDelay / 1000} seconds (${maxRetries} retries left)`);

    // Wait for the delay, then recursively call and return the result
    await new Promise((resolve) => setTimeout(resolve, retryDelay));
    return await getVideoHlsStream(assetId, maxRetries - 1);
  } else {
    console.log('Received HLS stream url', hls_url);
    return response;
  }
}

export async function getVideoAsset(assetId: string) {
  try {
    const response = await getVideoAssetAPI(assetId);
    return response;
  } catch (error) {
    console.log(error);
    throw 'Error getting video asset';
  }
}

export async function uploadScreeningCopy(
  teamId: string,
  yearId: string,
  submissionId: string,
  file: File,
  onProgress: (percentComplete: number) => void,
) {
  try {
    const movie = await uploadScreeningCopyAPI(teamId, yearId, submissionId, file, onProgress);
    return movie;
  } catch (error) {
    console.log(error);
    throw 'error uploading screening copy';
  }
}

export async function uploadTeamLogo(
  teamId: string,
  file: File,
  onProgress: (percentComplete: number) => void,
) {
  try {
    const terms = await uploadTeamLogoAPI(teamId, file, onProgress);
    return terms;
  } catch (error) {
    console.log(error);
    throw 'error uploading team log';
  }
}

export async function uploadContestMovie(
  teamId: string,
  yearId: string,
  file: File,
  onProgress: (percentComplete: number) => void,
) {
  try {
    const response = await uploadContestMovieAPI(teamId, yearId, file, onProgress);
    return response;
  } catch (error) {
    console.log(error);
    throw 'error uploading contest movie';
  }
}

export async function deleteContest(contestId: string) {
  try {
    const movie = await deleteContestAPI(contestId);
    return movie;
  } catch (error) {
    console.log(error);
    throw "Can't find Team Level";
  }
}

export async function saveAvatar(file: File, onProgress: (percentComplete: number) => void) {
  try {
    const terms = await saveAvatarAPI(file, onProgress);
    return terms;
  } catch (error) {
    console.log(error);
    throw "Can't save avatar";
  }
}

export async function uploadContestRelease(
  contestId: string,
  typeId: string,
  file: File,
  onProgress: (percentComplete: number) => void,
) {
  try {
    await uploadContestReleaseAPI(contestId, file, typeId, onProgress);
  } catch (error) {
    console.log(error);
    throw "Can't save release";
  }
}

export async function uploadSubmissionRelease(
  submissionId: string,
  typeId: string,
  file: File,
  onProgress: (percentComplete: number) => void,
): Promise<IAssetDTO> {
  try {
    const ret = await uploadSubmissionReleaseAPI(submissionId, file, typeId, onProgress);
    return ret;
  } catch (error) {
    console.log(error);
    throw "Can't save release";
  }
}

export async function deleteReleaseFromContest(contestId: string, releaseId: string) {
  try {
    await deleteReleaseFromContestAPI(contestId, releaseId);
  } catch (error) {
    console.log(error);
    throw "Can't find delete release";
  }
}

export async function deleteReleaseFromSubmission(submissionId: string, releaseId: string) {
  try {
    await deleteReleaseFromSubmissionAPI(submissionId, releaseId);
  } catch (error) {
    console.log(error);
    throw "Can't find delete release";
  }
}
